import React, { useMemo } from 'react';
import ContentManagementInterface, {
  type ContentItem,
} from '../../models/ContentManagementInterface';
import BackendService from '../../services/admin/BackendService';
import { Palette } from 'Palette';
import { StyledTypography } from '../styles/styles';
import ImageCarousel from '../template/ImageCarousel';
import LeftRightList from '../template/LeftRightList';
import StandardHeaderText from '../template/StandardHeaderText';
import Subtitle from '../template/Subtitle';
import Table from '../template/Table';
import TextBlock from '../template/TextBlock';
import HeroImage from '../template/HeroImage';
import ContentBlockEditor from './newContentManagement/ContentBlockEditor';

interface ReviewContentEditItemRendererProps {
  content: any;
  editMode: boolean;
  tone: keyof typeof Palette;
  draftContent: ContentManagementInterface;
  modelType: 'FundOpportunity' | 'FundUpdate';
  onChange?: (updated: any) => void;
}

export default function ReviewContentEditItemRenderer({
  content,
  editMode,
  tone,
  draftContent,
  modelType,
  onChange,
}: ReviewContentEditItemRendererProps) {
  const backendService = useMemo(() => new BackendService(), []);
  if (!content) {
    return null;
  }

  if (editMode) {
    return (
      <ContentBlockEditor
        block={content as ContentItem}
        onChange={(updated) => onChange?.(updated)}
        modelId={draftContent.relatedObjectId}
        modelType={modelType}
        backendService={backendService}
      />
    );
  }

  switch (content.type) {
    case 'StandardHeaderText':
      return (
        <StandardHeaderText {...content.properties} id={content.id} editMode={false} tone={tone} />
      );

    case 'Subtitle':
      return <Subtitle {...content.properties} id={content.id} tone={tone} />;

    case 'Text':
      return <TextBlock {...content.properties} id={content.id} tone={tone} />;

    case 'HeroImage':
      return <HeroImage {...content.properties} id={content.id} tone={tone} />;

    case 'ImageCarousel':
      return (
        <ImageCarousel
          {...content.properties}
          id={content.id}
          modelId={draftContent.relatedObjectId}
          modelType={modelType}
          editMode={false}
          tone={tone}
        />
      );

    case 'LeftRightList':
      return <LeftRightList {...content.properties} id={content.id} editMode={false} tone={tone} />;

    case 'Table':
      return <Table {...content.properties} id={content.id} editMode={false} tone={tone} />;

    default:
      return (
        <StyledTypography color="text.secondary">
          Unknown content type: {content.type}
        </StyledTypography>
      );
  }
}
